import React from "react";
import axios from "axios";

import { withStyles, makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import CallIcon from "@material-ui/icons/Call";
import AlternateEmailIcon from "@material-ui/icons/AlternateEmail";
import PermIdentityIcon from "@material-ui/icons/PermIdentity";
import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import DateRangePicker from "@wojtekmaj/react-daterange-picker";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useClipboard } from "use-clipboard-copy";
import jsPDF from "jspdf";

import Constants from "./../../Utils/Constants";
import { getToken, getHeaders } from "./../../Utils/Common";
import store from "./../../Common//storage";
import Header from "./../../assets/components/header";
import toastMaker from "./../../Common/toastmaker.js";

import Pagination from '@material-ui/lab/Pagination';
import Skeleton from '@material-ui/lab/Skeleton';

import "./../../index.css";
import "@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css";
import "react-calendar/dist/Calendar.css";

const columns = [
  { id: "sms", label: "Sent Message", minWidth: 100 },
  { id: "sender_id", label: "Sender Id", minWidth: 100 },
  {
    id: "name",
    label: `${store?.get('user')?.partner?.whitelabel_name} Contact`,
    minWidth: 50,
    align: "left",
  },
  {
    id: "campaign_name",
    label: "Campaign Name",
    minWidth: 50,
    align: "left",
  },
  {
    id: "created_at",
    label: "Send Time",
    minWidth: 190,
    align: "center",
  },
  {
    id: "link_clicks",
    label: `Link Clicks`,
    minWidth: 30,
    align: "left",
  },
];

const StyledTableCell = withStyles((theme) => ({
  body: {
    fontSize: 14,
    fontWeight: 500,
    padding: 15,
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    "&:nth-of-type(even)": {
      backgroundColor: "rgba(251, 220, 119, 0.28)",
    },
  },
}))(TableRow);

const useStyles = makeStyles({
  root: {
    width: "100%",
  },
  container: {
    maxHeight: "100%",
  },
});

const toLocalISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getDefaultDateRange = () => {
  const today = new Date();
  const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  return [from, today];
};

export default function StickyHeadTable(props) {
  const classes = useStyles();

  // No company config selected yet (e.g. deep-linked here, or just used
  // "Switch Company") - every request below needs a real c_id, so bounce to
  // the dashboard's company picker instead of firing requests with
  // c_id=undefined.
  React.useEffect(() => {
    if (!store.get('c_id')) {
      Constants.navigate(Constants.dashboard);
    }
  }, []);

  const [serverData, setServerData] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [pageCount, setPageCount] = React.useState(0);
  const [tableLoading, setTableLoading] = React.useState(false);
  const [statsLoading, setStatsLoading] = React.useState(false);
  const [hasGenerated, setHasGenerated] = React.useState(false);
  const [appliedFilters, setAppliedFilters] = React.useState(null);
  const [open, setOpen] = React.useState(0);
  const [dialogTitle, setDialogTitle] = React.useState('');
  const [dialogData, setDialogData] = React.useState('');
  const [mediaUrl, setMediaUrl] = React.useState('');
  const [total, setTotal] = React.useState('');
  const [failed, setFailed] = React.useState('');
  const [linkclicks, setLinkclicks] = React.useState('');
  const [unsubclick, setUnsubclick] = React.useState('');
  const [filterName, setFilterName] = React.useState('');
  const [filterDateRange, setFilterDateRange] = React.useState(getDefaultDateRange);
  const [filterSmsType, setFilterSmsType] = React.useState('all');
  const [campaignOptions, setCampaignOptions] = React.useState([]);
  const [campaignSearchLoading, setCampaignSearchLoading] = React.useState(false);
  const [csvLoading, setCsvLoading] = React.useState(false);
  const abortControllerRef = React.useRef(null);
  const statsAbortControllerRef = React.useRef(null);
  const campaignSearchAbortRef = React.useRef(null);

  React.useEffect(() => {
    const query = filterName.trim();
    if (!query || !store.get('c_id')) {
      setCampaignOptions([]);
      setCampaignSearchLoading(false);
      return;
    }
    const timer = setTimeout(() => {
      campaignSearchAbortRef.current?.abort();
      const controller = new AbortController();
      campaignSearchAbortRef.current = controller;
      setCampaignSearchLoading(true);

      const url = Constants.getApiPathWithConfigId(Constants.automationCampaignsApiPath)
        + `?per_page=10&name=${encodeURIComponent(query)}`;

      axios.get(url, { ...getHeaders(), signal: controller.signal })
        .then(response => {
          setCampaignOptions(response.data.data || []);
          setCampaignSearchLoading(false);
        }).catch(error => {
          if (axios.isCancel(error)) return;
          console.error(error);
          setCampaignSearchLoading(false);
        });
    }, 300);
    return () => clearTimeout(timer);
  }, [filterName]);

  const data = {
    labels: ["DELIVERED", "UNDELIVERABLE"],
    datasets: [
      {
        label: "Delivery Report",
        data: [total - failed, failed],
        backgroundColor: [
          "rgba(54, 162, 235, 0.6)",
          "rgba(255, 99, 132, 0.6)",
          // "rgba(255, 206, 86, 0.6)",
          // "rgba(75, 192, 192, 0.6)",
          // "rgba(153, 102, 255, 0.6)",
        ],
        borderColor: [
          "rgba(54, 162, 235, 1)",
          "rgba(255, 99, 132, 1)",
          // "rgba(255, 206, 86, 1)",
          // "rgba(75, 192, 192, 1)",
          // "rgba(153, 102, 255, 1)",
        ],
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "bottom",
      },
    },
  };

  const handleChange = (event, value) => {
    setPage(value);
  };

  const isFiltered = !!(appliedFilters && (appliedFilters.name.trim() || appliedFilters.dateRange?.[0] || appliedFilters.smsType !== 'all'));
  const displayedData = serverData || [];

  const handleGenerate = () => {
    setPage(1);
    setAppliedFilters({
      name: filterName,
      dateRange: filterDateRange,
      smsType: filterSmsType,
    });
    setHasGenerated(true);
  };

  const buildFilterQuery = () => {
    let query = '';
    if (appliedFilters?.name.trim())
      query += `&name=${encodeURIComponent(appliedFilters.name.trim())}`;
    if (appliedFilters?.dateRange?.[0])
      query += `&date_from=${toLocalISODate(appliedFilters.dateRange[0])}`;
    if (appliedFilters?.dateRange?.[1])
      query += `&date_to=${toLocalISODate(appliedFilters.dateRange[1])}`;
    if (appliedFilters?.smsType !== 'all')
      query += `&sms_type=${appliedFilters.smsType}`;
    return query;
  };

  // Table rows: cheap, LIMIT-bound query - fetched on its own so the table can
  // render without waiting on the slow full-scan stats aggregates below.
  React.useEffect(() => {
    if (!hasGenerated || !appliedFilters) return;
    refreshTable();
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [page, appliedFilters]);

  const refreshTable = () => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setTableLoading(true);

    const url = Constants.getApiPathWithConfigId(Constants.historyApiPath)
      + `?per_page=20&page=${page}&order_by=id&order=desc` + buildFilterQuery();

    axios.get(url, { ...getHeaders(), signal: controller.signal })
      .then(response => {
        setPageCount(response.data.meta.last_page);
        setServerData(response.data.data);
        setTableLoading(false);
      }).catch(error => {
        if (axios.isCancel(error)) return;
        console.error(error);
        setTableLoading(false);
      });
  };

  // Delivery/engagement stats: slow full-scan aggregates over the filtered set.
  // Only depends on the filters (not the page), so paging through results never
  // re-triggers this.
  React.useEffect(() => {
    if (!hasGenerated || !appliedFilters) return;
    refreshStats();
    return () => {
      statsAbortControllerRef.current?.abort();
    };
  }, [appliedFilters]);

  const refreshStats = () => {
    statsAbortControllerRef.current?.abort();
    const controller = new AbortController();
    statsAbortControllerRef.current = controller;

    setStatsLoading(true);

    const url = Constants.getApiPathWithConfigId(Constants.historyStatsApiPath)
      + `?order_by=id&order=desc` + buildFilterQuery();

    axios.get(url, { ...getHeaders(), signal: controller.signal })
      .then(response => {
        setTotal(response.data.meta.total);
        setFailed(response.data.meta.failed_count);
        setLinkclicks(response.data.meta.link_clicked);
        setUnsubclick(response.data.meta.unsubscribe_clicked);
        setStatsLoading(false);
      }).catch(error => {
        if (axios.isCancel(error)) return;
        console.error(error);
        setStatsLoading(false);
      });
  };

  const close = () => {
    setDialogTitle('')
    setDialogData('')
    setOpen(0)
  }

  const closeMedia = () => setMediaUrl('');

  const delivered    = total - failed;
  const deliveryRate = total > 0 ? ((delivered / total) * 100).toFixed(1) : '0.0';
  const clickRate    = total > 0 ? ((linkclicks / total) * 100).toFixed(1) : '0.0';

  const filterLabel = () => {
    const parts = [];
    if (!appliedFilters) return 'All time';
    if (appliedFilters.name.trim()) parts.push(`Search: "${appliedFilters.name.trim()}"`);
    if (appliedFilters.dateRange && appliedFilters.dateRange[0] && appliedFilters.dateRange[1]) {
      parts.push(`${new Date(appliedFilters.dateRange[0]).toLocaleDateString('en-AU')} - ${new Date(appliedFilters.dateRange[1]).toLocaleDateString('en-AU')}`);
    }
    if (appliedFilters.smsType !== 'all') parts.push(`Type: ${appliedFilters.smsType.toUpperCase()}`);
    return parts.length ? parts.join('  |  ') : 'All time';
  };

  const clipboard = useClipboard({
    onSuccess() { toastMaker.makeToast("Report copied to clipboard!", { variant: "success" }); },
    onError()   { toastMaker.makeToast("Failed to copy report.", { variant: "error" }); },
  });

  // The table only holds the current page, so page through the history API
  // with the applied filters to export every matching message.
  const fetchAllHistoryRows = async () => {
    const all = [];
    let currentPage = 1;
    let lastPage = 1;
    do {
      const url = Constants.getApiPathWithConfigId(Constants.historyApiPath)
        + `?per_page=500&page=${currentPage}&order_by=id&order=desc` + buildFilterQuery();
      const response = await axios.get(url, getHeaders());
      all.push(...(response.data.data || []));
      lastPage = response.data.meta?.last_page || 1;
      currentPage++;
    } while (currentPage <= lastPage);
    return all;
  };

  const clickCount = raw => {
    try { return JSON.parse(raw)?.length || 0; } catch (e) { return 0; }
  };

  const downloadCSV = async () => {
    setCsvLoading(true);
    let allRows;
    try {
      allRows = await fetchAllHistoryRows();
    } catch (error) {
      console.error(error);
      toastMaker.makeToast("Failed to download CSV.", { variant: "error" });
      setCsvLoading(false);
      return;
    }
    setCsvLoading(false);

    const escape = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = [
      ['SMS Automation History Report'],
      ['Filter', filterLabel()],
      [''],
      ['DELIVERY SUMMARY'],
      ['Total Sent',    total],
      ['Delivered',     delivered],
      ['Delivery Rate', `${deliveryRate}%`],
      ['Undeliverable', failed],
      [''],
      ['ENGAGEMENT'],
      ['Link Clicks',        linkclicks],
      ['Click Rate',         `${clickRate}%`],
      ['Unsubscribe Clicks', unsubclick],
      [''],
      ['MESSAGE LOG'],
      ['Contact', 'Sender ID', 'Send Date', 'Link Clicks', 'Unsub Clicks', 'Message'],
      ...allRows.map(r => [
        r.phone_number,
        r.sender_id,
        new Date(r.created_at).toLocaleString('en-GB'),
        clickCount(r.link_click_data),
        clickCount(r.unsub_link_click_data),
        r.sms,
      ]),
    ];
    // BOM so Excel opens the file as UTF-8 (emoji / non-Latin message text).
    const csv  = '﻿' + rows.map(r => r.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `automation-history.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPDF = async () => {
    const doc = new jsPDF();

    // ── Header bar ──
    doc.setFillColor(18, 38, 67);
    doc.rect(0, 0, 210, 22, 'F');
    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('SMS Automation History Report', 105, 14, { align: 'center' });
    doc.setTextColor(0, 0, 0);

    let y = 32;

    // ── Filter label ──
    if (isFiltered) {
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(107, 108, 126);
      doc.text(`Filter: ${filterLabel()}`, 15, y);
      doc.setTextColor(0, 0, 0);
      y += 9;
    }

    // ── Section header helper ──
    const sectionHeader = title => {
      doc.setFontSize(8);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(107, 108, 126);
      doc.text(title.toUpperCase(), 15, y);
      doc.setTextColor(0, 0, 0);
      y += 4;
      doc.setDrawColor(231, 231, 237);
      doc.line(15, y, 125, y);
      y += 5;
    };

    // ── Stat row helper (label left, value right) ──
    const statRow = (label, value) => {
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(107, 108, 126);
      doc.text(label, 20, y);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(39, 40, 51);
      doc.text(String(value), 95, y);
      doc.setTextColor(0, 0, 0);
      y += 7;
    };

    sectionHeader('Delivery');
    statRow('Total',           total);
    statRow('Delivered',       delivered);
    statRow('Undeliverable',   failed);
    statRow('Delivery Rate',   `${deliveryRate}%`);

    y += 3;
    sectionHeader('Engagement');
    statRow('Link Clicks',        linkclicks);
    statRow('Click Rate',         `${clickRate}%`);
    statRow('Unsubscribe Clicks', unsubclick);

    // ── Pie chart ──
    y += 6;
    const canvas = document.createElement('canvas');
    canvas.width = 300; canvas.height = 300;
    document.body.appendChild(canvas);
    new ChartJS(canvas.getContext('2d'), {
      type: 'pie',
      data: {
        labels: ['Delivered', 'Undeliverable'],
        datasets: [{ data: [delivered, failed], backgroundColor: ['#ADD8E6', '#FFB6C1'] }],
      },
      options: { animation: false, plugins: { legend: { position: 'bottom', labels: { font: { size: 14 } } } } },
    });
    await new Promise(r => setTimeout(r, 500));
    doc.addImage(canvas.toDataURL('image/png'), 'PNG', 30, y, 90, 90);
    canvas.remove();

    // ── Footer ──
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(186, 186, 186);
    const brandName = 'VYBRNT SMS';
    const reportDate = new Date().toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
    doc.text(`Report Date: ${reportDate}`, 105, 281, { align: 'center' });
    doc.text(`Generated by ${brandName}`, 105, 287, { align: 'center' });

    doc.save('automation-history-report.pdf');
  };

  const copyReport = () => {
    if (!clipboard.isSupported()) {
      toastMaker.makeToast("Copy not supported by this browser.", { variant: "error" });
      return;
    }
    const fmt   = n => Number(n || 0).toLocaleString();
    const divider = '-'.repeat(44);
    const pad   = (label, value) => `  ${label.padEnd(22)}${value}`;
    const reportDate = new Date().toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });

    const narrative = total > 0
      ? `Out of ${fmt(total)} messages sent, ${fmt(delivered)} were successfully delivered - a ${deliveryRate}% delivery rate. ${fmt(linkclicks)} recipient${linkclicks !== 1 ? 's' : ''} clicked a link (${clickRate}% click rate)${unsubclick > 0 ? `, and ${fmt(unsubclick)} unsubscribed` : ''}.`
      : `No messages found for the selected filters.`;

    const text = [
      `SMS Automation History Report`,
      `Report Date: ${reportDate}`,
      isFiltered ? `Filter:      ${filterLabel()}` : null,
      ``,
      narrative,
      ``,
      divider,
      `DELIVERY`,
      pad('Total Sent',     fmt(total)),
      pad('Delivered',      `${fmt(delivered)}  (${deliveryRate}%)`),
      pad('Undeliverable',  fmt(failed)),
      ``,
      `ENGAGEMENT`,
      pad('Link Clicks',        `${fmt(linkclicks)}  (${clickRate}%)`),
      pad('Unsubscribe Clicks', fmt(unsubclick)),
      divider,
      `Generated by VYBRNT SMS`,
    ].filter(l => l !== null).join('\n');

    clipboard.copy(text);
  };

  return (
    <div className="panal-viewport middle-container">
      <Header name={"SMS Automation History"} />

      {/* ── Filter bar + action buttons ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
        <Autocomplete
          freeSolo
          filterOptions={(options) => options}
          options={campaignOptions}
          loading={campaignSearchLoading}
          getOptionLabel={(option) => typeof option === 'string' ? option : (option?.name || '')}
          inputValue={filterName}
          onInputChange={(event, newValue) => setFilterName(newValue)}
          onChange={(event, newValue) => {
            setFilterName(typeof newValue === 'string' ? newValue : (newValue?.name || ''));
          }}
          style={{ width: '220px' }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search campaign"
              variant="outlined"
              onKeyDown={e => { if (e.key === 'Enter') handleGenerate(); }}
              InputProps={{
                ...params.InputProps,
                style: { height: '44px', fontSize: '13px' },
                endAdornment: (
                  <>
                    {campaignSearchLoading ? <CircularProgress color="inherit" size={14} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <DateRangePicker
          value={filterDateRange}
          onChange={v => setFilterDateRange(v)}
          clearIcon="✕"
          calendarIcon="📅"
        />
        <select
          value={filterSmsType}
          onChange={e => setFilterSmsType(e.target.value)}
          style={{
            border: '1px solid #BABABA', borderRadius: '4px',
            padding: '6px 28px 6px 10px', fontSize: '13px', outline: 'none',
            height: '44px', boxSizing: 'border-box',
            fontFamily: 'inherit', color: '#272833', background: '#fff',
            appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
            backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M0 0l5 6 5-6z' fill='%236B6C7E'/></svg>")`,
            backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center',
            cursor: 'pointer',
          }}
        >
          <option value="all">All Types</option>
          <option value="sms">SMS</option>
          <option value="mms">MMS</option>
        </select>
        <p
          className="c-button c-button--primary text-uppercase text-light m-0"
          style={{ fontSize: '11px', opacity: (tableLoading || statsLoading) ? 0.7 : 1, cursor: (tableLoading || statsLoading) ? 'default' : 'pointer' }}
          onClick={() => { if (!tableLoading && !statsLoading) handleGenerate(); }}
        >
          {(tableLoading || statsLoading) ? 'Generating…' : 'Generate'}
        </p>
        {(filterName.trim() || (filterDateRange && filterDateRange[0]) || filterSmsType !== 'all') && (
          <span
            style={{ fontSize: '12px', color: '#45A9F0', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => { setFilterName(''); setFilterDateRange(null); setFilterSmsType('all'); }}
          >
            Clear
          </span>
        )}
        {hasGenerated && !statsLoading && isFiltered && total > 0 && (
          <span style={{ fontSize: '12px', color: '#6B6C7E' }}>
            {total} result{total !== 1 ? 's' : ''}
          </span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
          <p className="c-button c-button--secondary text-uppercase text-light m-0"
            style={{ fontSize: '11px', opacity: (!hasGenerated || tableLoading || statsLoading || displayedData.length === 0) ? 0.5 : 1, cursor: (!hasGenerated || tableLoading || statsLoading || displayedData.length === 0) ? 'default' : 'pointer' }}
            onClick={() => { if (hasGenerated && !tableLoading && !statsLoading && displayedData.length > 0) downloadPDF(); }}>Download PDF</p>
          <p className="c-button c-button--secondary text-uppercase text-light m-0"
            style={{ fontSize: '11px', opacity: (!hasGenerated || tableLoading || statsLoading || csvLoading || displayedData.length === 0) ? 0.5 : 1, cursor: (!hasGenerated || tableLoading || statsLoading || csvLoading || displayedData.length === 0) ? 'default' : 'pointer' }}
            onClick={() => { if (hasGenerated && !tableLoading && !statsLoading && !csvLoading && displayedData.length > 0) downloadCSV(); }}>{csvLoading ? 'Preparing…' : 'Download CSV'}</p>
          <p className="c-button c-button--secondary text-uppercase text-light m-0"
            style={{ fontSize: '11px', opacity: (!hasGenerated || tableLoading || statsLoading || displayedData.length === 0) ? 0.5 : 1, cursor: (!hasGenerated || tableLoading || statsLoading || displayedData.length === 0) ? 'default' : 'pointer' }}
            onClick={() => { if (hasGenerated && !tableLoading && !statsLoading && displayedData.length > 0) copyReport(); }}>Copy Report</p>
        </div>
      </div>

      <div className="d-flex flex-row">
      {!hasGenerated ? (
        <Paper className="p-4 flex-grow-1" style={{ textAlign: 'center', color: '#6B6C7E' }}>
          <p style={{ fontSize: '14px', margin: 0 }}>
            Select your filters and click <strong>Generate</strong> to view the report.
          </p>
        </Paper>
      ) : (
      <>
        <Paper className={` p-2 `} style={{width: '30%'}}>
              {statsLoading ? (
                <>
                  <Skeleton variant="text" height={24} width="90%" />
                  <Skeleton variant="text" height={24} width="70%" />
                  <Skeleton variant="text" height={24} width="80%" />
                  <Skeleton variant="text" height={24} width="60%" />
                  <Skeleton variant="circle" width={200} height={200} style={{ margin: '30px auto' }} />
                </>
              ) : (
              <>
                  <div className="d-flex">
                    <p style={{maxWidth: '400px', fontWeight: 'normal', fontSize: '14px', margin: 0, width: '50%'}}>Total: { total }</p>
                    <p style={{maxWidth: '400px', fontWeight: 'normal', fontSize: '14px', margin: 0, width: '50%'}}>Delivered: { total - failed }</p>
                  </div>
                  <div className="d-flex">
                    <p style={{maxWidth: '400px', fontWeight: 'normal', fontSize: '14px', margin: 0, width: '50%'}}>Undeliverable: { failed }</p>
                    <p style={{maxWidth: '400px', fontWeight: 'normal', fontSize: '14px', margin: 0, width: '50%'}}>Percentage: {
                      total > 0 ? (((total - failed)/total) * 100).toFixed(2) : '0.00'
                    }%</p>
                  </div>
                  <div className="d-flex">
                    <p style={{maxWidth: '400px', fontWeight: 'normal', fontSize: '14px', margin: 0, width: '50%'}}>Link Click: { linkclicks }</p>
                    <p style={{maxWidth: '400px', fontWeight: 'normal', fontSize: '14px', margin: 0, width: '50%'}}>Unsubscribe Click: { unsubclick } </p>
                  </div>
                  <div className="d-flex">
                    <p style={{maxWidth: '800px', fontWeight: 'normal', fontSize: '14px', margin: 0, width: '50%'}}>Link Click Rate: { total > 0 ? ((linkclicks/total) * 100).toFixed(2) : '0.00' }%</p>
                    {/* <p style={{maxWidth: '400px', fontWeight: 'normal', fontSize: '14px', margin: 0, width: '50%'}}>Unsubscribe Click Rate: { ((unsubclick/total) * 100).toFixed(2) } </p> */}
                  </div>
                  <div style={{width: '75%', margin: '30px auto'}}>
                    <Pie data={data} options={options} />
                  </div>
              </>
              )}
              </Paper>
        <Paper className={`${classes.root} pb-2 flex-grow-1`}>
          <TableContainer component={Paper}>
            <Table className={classes.table} aria-label="customized table">
              <TableHead>
                <TableRow id="table-head_rw">
                  <StyledTableCell className="table-hd">
                    Sent Message
                  </StyledTableCell>
                  <StyledTableCell className="table-hd">
                    Sender ID
                  </StyledTableCell>
                  <StyledTableCell className="table-hd">
                    {/* {store?.get('user')?.partner?.whitelabel_name}  */}
                    Contact
                  </StyledTableCell>
                  <StyledTableCell className="table-hd">
                    Campaign
                  </StyledTableCell>
                  <StyledTableCell className="table-hd">
                    Send Date,Time
                  </StyledTableCell>
                  <StyledTableCell className="table-hd">
                    Link Clicks
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableLoading && Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    {columns.map((column) => (
                      <StyledTableCell key={column.id}>
                        <Skeleton variant="text" height={20} />
                      </StyledTableCell>
                    ))}
                  </TableRow>
                ))}
                {!tableLoading && displayedData.length === 0 && (
                  <TableRow>
                    <StyledTableCell colSpan={5} style={{ textAlign: 'center', fontWeight: 'normal', color: '#BABABA' }}>
                      {isFiltered ? 'No messages match the current filters.' : 'No data found.'}
                    </StyledTableCell>
                  </TableRow>
                )}
                {!tableLoading && displayedData.map((row) => {
                  const rowId = row.id;
                  // console.log(row, 'row')
                  return (
                    <StyledTableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={rowId}
                    >
                      {columns.map((column) => {
                        const value = row[column.id];
                        if (column.id === "sms") {
                          return (
                            <StyledTableCell
                              component="td"
                              scope="row"
                              key={column.id}
                            >
                              <div style={{
                                maxWidth: '340px',
                                background: '#F4F7FF',
                                border: '1px solid #D6E4FF',
                                borderRadius: '10px',
                                padding: '8px 12px',
                                fontSize: '13px',
                                lineHeight: '1.6',
                                color: '#272833',
                                wordBreak: 'break-word',
                                fontWeight: 'normal',
                              }}>
                                <span style={{
                                  display: 'inline-block',
                                  background: row.sms_type === 'mms' ? '#45A9F0' : '#6B6C7E',
                                  color: '#fff',
                                  fontSize: '10px',
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.04em',
                                  padding: '1px 7px',
                                  borderRadius: '10px',
                                  marginBottom: '6px',
                                }}>
                                  {row.sms_type === 'mms' ? 'MMS' : 'SMS'}
                                </span>
                                {row.sms_type === 'mms' && row.subject && (
                                  <div style={{ fontWeight: 700, marginTop: '4px', marginBottom: '4px' }}>
                                    Subject: {row.subject}
                                  </div>
                                )}
                                {row.sms_type === 'mms' && row.url && (
                                  <div style={{ marginBottom: '6px' }}>
                                    <img
                                      src={row.url}
                                      alt="MMS media"
                                      onClick={() => setMediaUrl(row.url)}
                                      style={{
                                        maxWidth: '100%',
                                        maxHeight: '160px',
                                        display: 'block',
                                        borderRadius: '6px',
                                        cursor: 'zoom-in',
                                      }}
                                    />
                                  </div>
                                )}
                                <div><span>{value}</span></div>
                              </div>
                            </StyledTableCell>
                          );
                        } else if (column.id === "name") {
                          return (
                            <StyledTableCell className="text-blue">
                              {/* <div className="template-info">
                                <div className="template-info__item">
                                  <PermIdentityIcon
                                    fontSize="small"
                                    style={{ fontSize: "0.9rem" }}
                                  />
                                  <span
                                    style={{
                                      fontWeight: "700",
                                      marginLeft: "5px",
                                      color: "#000",
                                    }}
                                  >
                                    {" "}
                                    {value}{" "}
                                  </span>
                                </div>
                              </div> */}
                              <a href={Constants.getAcContactUrl(row.ac_id)} target="_blank" className="text-blue">
                                {row.phone_number}
                              </a>
                            </StyledTableCell>
                          );
                        } else if (column.id === "created_at") {
                          return (
                            <StyledTableCell>
                              <div className="send-time">
                                <span>
                                  {new Date(value).toLocaleString('en-GB', {
                                                                                                            day: 'numeric',
                                                                                                            month: 'numeric',
                                                                                                            year: 'numeric',
                                                                                                            hour: 'numeric',
                                                                                                            minute: '2-digit',
                                                                                                            hour12: true
                                                                                                          })}
                                </span>
                              </div>
                            </StyledTableCell>
                          );
                        } else if (column.id=="link_clicks") {
                          return (
                            <StyledTableCell className="text-blue">
                              <a href="#" className="text-success" onClick={() => {
                            setDialogTitle('Link Clicks')
                            setDialogData(row?.link_click_data)
                            setOpen(1)
                          }} 
                          title="Link clicks"
                          >
                              {JSON.parse(row?.link_click_data)?.length || 0}
                          </a> | <a href="#" className="text-danger" onClick={() => {
                            setDialogTitle('Unsubscribe Clicks')
                            setDialogData(row?.unsub_link_click_data)
                            setOpen(1)
                          }}
                          title="Unsubscribe link clicks"
                          >
                              {JSON.parse(row?.unsub_link_click_data)?.length || 0}
                          </a> 
                            </StyledTableCell>
                          );
                        } 
                        else {
                          return (
                            <StyledTableCell >
                              {column.format && typeof value === "number"
                                ? column.format(value)
                                : value}
                            </StyledTableCell>
                          );
                        }
                      })}
                    </StyledTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {pageCount > 0 && <div className={`${classes.root} mt-3 mb-3 d-flex justify-content-end`}>
                    <Pagination 
                      count={pageCount} 
                      page={page} 
                      onChange={handleChange} 
                      variant="outlined" 
                      shape="rounded"
                      />
                  </div>}
        </Paper>
      </>
      )}
        <Dialog
                  onClose={close}
                  aria-labelledby="simple-dialog-title"
                  open={open}
                >
                  <DialogTitle id="simple-dialog-title">
                    {dialogTitle}
                  </DialogTitle>
                  <DialogContent>
                    <div className="form-groups">
                      {
                        dialogData ? <>
                          <TableContainer component={Paper}>
                            <Table className={classes.table} aria-label="customized table">
                              <TableHead>
                                <TableRow id="table-head_rw">
                                  <StyledTableCell className="table-hd">
                                    Link
                                  </StyledTableCell>
                                  <StyledTableCell className="table-hd">
                                    Date
                                  </StyledTableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {dialogData && JSON.parse(dialogData)?.map((r, i) => <TableRow key={i}>
                                    <StyledTableCell className="">
                                      <div className="speech-bubble" style={{maxWidth: '400px', fontWeight: 'normal', fontSize: '14px'}}>
                                        <span>
                                          <a href={r?.eventDetails?.link} target="_blank" title={r?.eventDetails?.link}>
                                            {r?.eventDetails?.link.substr(0, 40)}...
                                          </a>
                                        </span>
                                      </div>
                                    </StyledTableCell>
                                    <StyledTableCell className="">
                                      <div className="speech-bubble" style={{maxWidth: '400px', fontWeight: 'normal', fontSize: '14px'}}>
                                        <span>{new Date((r?.timestamp * 1000)).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                                      </div>
                                    </StyledTableCell>
                                  </TableRow>
                                ) 
                                }
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </> : <span>No data found.</span>
                      }
                    </div>
                  </DialogContent>
                  <DialogActions style={{ padding: "8px 24px 24px" }}>
                    <span
                      className="c-button text-uppercase"
                      style={{ marginRight: "10px" }}
                      onClick={close}
                    >
                      Close
                    </span>
                  </DialogActions>
        </Dialog>
        <Dialog
                  onClose={closeMedia}
                  aria-labelledby="media-dialog-title"
                  open={!!mediaUrl}
                  maxWidth="md"
                >
                  <DialogTitle id="media-dialog-title">
                    MMS Media
                  </DialogTitle>
                  <DialogContent>
                    {mediaUrl && (
                      <img
                        src={mediaUrl}
                        alt="MMS media"
                        style={{ maxWidth: '100%', maxHeight: '70vh', display: 'block', margin: '0 auto' }}
                      />
                    )}
                  </DialogContent>
                  <DialogActions style={{ padding: "8px 24px 24px" }}>
                    <span
                      className="c-button text-uppercase"
                      style={{ marginRight: "10px" }}
                      onClick={closeMedia}
                    >
                      Close
                    </span>
                  </DialogActions>
        </Dialog>
      </div>
    </div>
  );
}
